// src/pages/MasjidPublicHome.jsx
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { collection, onSnapshot, query, where, orderBy, limit } from "firebase/firestore";
import { dbAdmin } from "../firebaseConfig";
import { toYMD, expandRecurringRules } from "../utils/recurringUtils";
import { useLocation, useNavigate } from "react-router-dom";

import MasjidAzanCard from "../components/MasjidAzanCard";

import "../styles/Body.css";
import "../styles/MasjidPublic.css";

import {
    FiPhone,
    FiMail,
    FiMessageCircle,
    FiGlobe,
    FiMapPin,
    FiUsers,
    FiHeart,
    FiBookOpen,
    FiCopy,
    FiCheck,
    FiCreditCard,
    FiArrowUp
} from "react-icons/fi";

import { FaWhatsapp, FaFacebook, FaInstagram, FaYoutube } from "react-icons/fa";

import PublicKempenFeatured from "../components/PublicKempenFeatured";
import FbTimelineWidget from "../components/FbTimelineWidget";

import { getHariLong, getTarikh, capitalizeWords } from "../utils/formatHelpers";
import { FaLocationPin } from "react-icons/fa6";

const getStartMs = (k) => {
    if (!k) return 0;
    if (k.start?.seconds) return k.start.seconds * 1000;
    if (typeof k.start?.toDate === "function") return k.start.toDate().getTime();
    if (k.start instanceof Date) return k.start.getTime();
    return 0;
};

const cleanPhone = (p = "") => String(p).replace(/[^\d]/g, "");

const waLink = (phone, text = "") => {
    const n = cleanPhone(phone);
    if (!n) return null;
    return `https://wa.me/6${n}?text=${encodeURIComponent(text)}`;
};




const DEFAULT_CATS = ["pengurusan", "imam", "bilal"];
const normalizeKey = (s = "") => String(s).toLowerCase().trim();

const titleCase = (s = "") =>
    String(s)
        .split(" ")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");

const buildPegawaiGroupsForPublic = (pegawai = []) => {
    const map = new Map();

    const ensure = (key, label) => {
        if (!map.has(key)) map.set(key, { key, label, items: [] });
        return map.get(key);
    };

    ensure("pengurusan", "PENGURUSAN");
    ensure("imam", "IMAM");
    ensure("bilal", "BILAL");

    pegawai.forEach((p) => {
        const cat = normalizeKey(p.category);
        if (cat && !DEFAULT_CATS.includes(cat)) {
            ensure(cat, titleCase(p.label || cat).toUpperCase());
        }
    });

    pegawai.forEach((p) => {
        const cat = normalizeKey(p.category);
        const roles = new Set(p.roles || [cat]);
        const baseRank = Number(p.hirarki ?? 99);

        ensure(cat, titleCase(p.label || cat).toUpperCase()).items.push({
            ...p,
            __rank: baseRank,
        });

        if (roles.has("imam") && cat !== "imam") {
            ensure("imam", "IMAM").items.push({ ...p, __rank: 1 });
        }
        if (roles.has("bilal") && cat !== "bilal") {
            ensure("bilal", "BILAL").items.push({ ...p, __rank: 1 });
        }
    });

    map.forEach((g) => g.items.sort((a, b) => (a.__rank ?? 99) - (b.__rank ?? 99)));

    const customs = [...map.values()].filter((g) => !DEFAULT_CATS.includes(g.key));
    return [map.get("pengurusan"), map.get("imam"), map.get("bilal"), ...customs].filter(
        (g) => g && g.items.length > 0
    );
};

const waShareLink = (text, url = "") => {
    const msg = url ? `${text}\n${url}` : text;
    return `https://wa.me/?text=${encodeURIComponent(msg)}`;
};


export default function MasjidPublicHome() {
    const outlet = useOutletContext() || {};
    const masjid = outlet?.masjid || null;

    const masjidId = masjid?.id || outlet?.masjidId || "";  // penting

    const [subSingles, setSubSingles] = useState([]);       // /masajid/{id}/kuliah
    const [subRecurring, setSubRecurring] = useState([]);   // expanded dari /kuliahRecurring


    // ✅ hooks MUST be called regardless of masjid exists
    const [activeEtvHero, setActiveEtvHero] = useState(null);
    const [activeEtvIndex, setActiveEtvIndex] = useState(0);

    const mapRef = useRef(null);
    const pengurusanRef = useRef(null);
    const hubungiRef = useRef(null);
    const bankRef = useRef(null);
    const kuliahRef = useRef(null);
    const mediaRef = useRef(null);
    const kempenRef = useRef(null);
    const stripRef = useRef(null);
    const autoScrollPausedRef = useRef(false);
    const pauseTimerRef = useRef(null);
    const zoomPausedRef = useRef(false);

    const [etvZoomOpen, setEtvZoomOpen] = useState(false);

    const [copiedIndex, setCopiedIndex] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();



    // ===== safe defaults supaya hooks sentiasa konsisten
    const p = useMemo(() => (masjid?.profail ? masjid.profail : {}), [masjid]);

    // ===== NAMA/LOGO/BANNER (fallback kalau context tak ada)
    const masjidName = useMemo(() => {
        const ctxMasjidName = outlet?.masjidName;
        return ctxMasjidName || capitalizeWords(p?.nama || masjid?.nama || "Masjid");
    }, [outlet?.masjidName, p?.nama, masjid?.nama]);

    const masjidLogo = useMemo(() => {
        const ctxMasjidLogo = outlet?.masjidLogo;
        return (
            ctxMasjidLogo ||
            p?.logo ||
            p?.logoJa ||
            p?.gambar ||
            masjid?.logo ||
            masjid?.gambar ||
            ""
        );
    }, [outlet?.masjidLogo, p?.logo, p?.logoJa, p?.gambar, masjid?.logo, masjid?.gambar]);

    const bannerImg = useMemo(() => {
        const ctxBannerImg = outlet?.bannerImg;
        return ctxBannerImg || p?.gambar || masjid?.gambar || masjidLogo;
    }, [outlet?.bannerImg, p?.gambar, masjid?.gambar, masjidLogo]);

    // ===== ALAMAT
    const addressLine = useMemo(() => {
        return [
            p?.alamat || masjid?.alamat || "",
            p?.poskod || masjid?.poskod || "",
            p?.bandar || masjid?.bandar || "",
            p?.negeri || masjid?.negeri || "",
        ]
            .filter(Boolean)
            .join(", ");
    }, [
        p?.alamat,
        p?.poskod,
        p?.bandar,
        p?.negeri,
        masjid?.alamat,
        masjid?.poskod,
        masjid?.bandar,
        masjid?.negeri,
    ]);

    // ===== CONTACT (BASIC)
    const phone = p?.no_tel || masjid?.no_tel || "";
    const email = p?.emel || masjid?.emel || "";

    // ===== CONTACT MODE (whatsapp/messenger)
    const contactMode = p?.contactMode || "whatsapp";
    const whatsappNo = p?.whatsapp || phone;
    const facebookLink = p?.facebook || "";

    const contactLink = useMemo(() => {
        if (contactMode === "messenger") return facebookLink || null;
        if (!whatsappNo) return null;
        return waLink(whatsappNo, `Assalamualaikum. Saya ingin bertanya tentang ${masjidName}.`);
    }, [contactMode, facebookLink, whatsappNo, masjidName]);

    const hasHubungi = Boolean(phone || contactLink || email);
    const hasMediaSosial = Boolean(p?.facebook || p?.instagram || p?.youtube);


    // ===== KEMPEN (public)
    const kempenList = useMemo(() => {
        // ✅ ONLY ambil senarai kempen, jangan ambil object kempen
        const raw =
            masjid?.kempen?.senarai ??
            masjid?.aktiviti?.kempen ??
            [];

        const arr = Array.isArray(raw)
            ? raw
            : raw && typeof raw === "object"
                ? Object.values(raw)
                : [];

        // ✅ Pastikan item memang object kempen (bukan boolean/toyyib/etc)
        return arr
            .filter((k) => k && typeof k === "object" && !Array.isArray(k))
            .filter((k) => (k.aktif ?? k.active ?? false) === true); // default false
    }, [masjid?.kempen?.senarai, masjid?.aktiviti?.kempen]);

    const hasKempen = kempenList.length > 0;


    const [showToTop, setShowToTop] = useState(false);

    useEffect(() => {
        const onScroll = () => {
            // mobile sahaja
            const isMobile = window.innerWidth <= 900;
            if (!isMobile) {
                setShowToTop(false);
                return;
            }
            setShowToTop(window.scrollY > 400);
        };

        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll(); // init
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };





    const extractIframeSrc = (html = "") => {
        if (!html) return null;
        const match = String(html).match(/src="([^"]+)"/);
        return match ? match[1] : null;
    };

    const gembed = useMemo(() => extractIframeSrc(p?.gmaps), [p?.gmaps]);

    // ===== DERMA (QR + bank)
    const dermaQr = p?.qr || null;
    const bankList = useMemo(() => (Array.isArray(p?.bankAccounts) ? p.bankAccounts : []), [p?.bankAccounts]);
    const hasBank = Boolean(dermaQr || bankList.length > 0);

    // ===== PEGAWAI
    const pegawaiGroups = useMemo(
        () => buildPegawaiGroupsForPublic(Array.isArray(p?.pegawai) ? p.pegawai : []),
        [p?.pegawai]
    );
    const hasPegawai = pegawaiGroups.length > 0;

    // ===== KULIAH


    useEffect(() => {
        if (!masjidId) return;

        const now = new Date();
        const qy = query(
            collection(dbAdmin, "masajid", masjidId, "kuliah"),
            where("start", ">", now),
            orderBy("start", "asc"),
            limit(30)
        );

        return onSnapshot(
            qy,
            (snap) => {
                const rows = snap.docs.map((d) => {
                    const data = d.data() || {};
                    const start =
                        data.start?.toDate ? data.start.toDate()
                            : (data.start?.seconds ? new Date(data.start.seconds * 1000) : data.start);

                    return {
                        ...data,
                        masjidId,
                        eventId: data.eventId || d.id,
                        start,
                    };
                });
                setSubSingles(rows);
            },
            (err) => console.error("❌ subcollection kuliah error:", err)
        );
    }, [masjidId]);

    useEffect(() => {
        if (!masjidId) return;

        const today = toYMD(new Date());
        const qy = query(
            collection(dbAdmin, "masajid", masjidId, "kuliahRecurring"),
            where("repeatEnd", ">=", today),
            orderBy("repeatEnd", "asc"),
            limit(200)
        );

        return onSnapshot(
            qy,
            (snap) => {
                const nowMs = Date.now();

                // EXDATE dari doc masjid (kau simpan exceptions dalam aktiviti)
                const exList = masjid?.aktiviti?.kuliahRecurringExceptions || [];
                const exSet = new Set(
                    exList
                        .filter((x) => x?.seriesId && x?.dateYMD)
                        .map((x) => `${x.seriesId}__${x.dateYMD}`)
                );

                const rules = snap.docs.map((d) => {
                    const r = d.data() || {};
                    return { ...r, seriesId: r.seriesId || d.id, masjidId };
                });


                // expand 90 hari macam Body kau
                const expandedRaw = rules.flatMap((r) => expandRecurringRules([r], masjidId, nowMs, 90));

                // apply EXDATE (hide tarikh yang dipadam satu)
                const expanded = expandedRaw.filter((ev) => {
                    const ymd = toYMD(ev.start);
                    const key = `${ev.seriesId || ev.seriesKey}__${ymd}`;
                    return !exSet.has(key);
                });

                setSubRecurring(expanded);
                console.log("recurring expanded:", expanded.length, expanded.slice(0, 3));

            },
            (err) => console.error("❌ subcollection kuliahRecurring error:", err)
        );
    }, [masjidId, masjid?.aktiviti?.kuliahRecurringExceptions]);







    const masjidSlug = useMemo(
        () => p?.slug || masjid?.slug || masjidId,
        [p?.slug, masjid?.slug, masjidId]
    );

    // ✅ helper: normalize tarikh event (support Date + Firestore Timestamp)
    const getEventDate = (k) => {
        if (!k) return null;

        if (k.date instanceof Date) return k.date;

        // Firestore Timestamp
        if (k.start?.toDate) return k.start.toDate();
        if (k.start?.seconds) return new Date(k.start.seconds * 1000);

        if (k.start instanceof Date) return k.start;

        // fallback from startMs
        const ms = getStartMs(k);
        return ms ? new Date(ms) : null;
    };

    const kuliahUpcoming = useMemo(() => {
        const now = Date.now();

        const legacyList = Array.isArray(masjid?.aktiviti?.kuliah)
            ? masjid.aktiviti.kuliah
            : [];

        const merged = [
            ...legacyList.map(k => ({ ...k, masjidId })),
            ...subSingles.map(k => ({ ...k, masjidId })),
            ...subRecurring.map(k => ({ ...k, masjidId })),
        ];

        const map = new Map();

        const put = (k) => {
            const d = getEventDate(k);
            const sid = k?.seriesId || k?.seriesKey;

            // ✅ recurring dedupe key
            if (sid && d) {
                const kk = `${masjidId}|${sid}__${toYMD(d)}`;
                if (!map.has(kk)) map.set(kk, k);
                return;
            }

            const startMs = getStartMs(k);
            if (!startMs) return;

            const kk = k.eventId
                ? `${masjidId}|${k.eventId}`
                : `${masjidId}|${startMs}|${k.title || ""}|${k.speaker || ""}`;

            if (!map.has(kk)) map.set(kk, k);
        };

        merged.forEach(put);

        return Array.from(map.values())
            .map((k) => {
                const d = getEventDate(k);
                return {
                    ...k,
                    date: d,
                    programText: k.program || k.title || "Kuliah",
                    ustazText: k.speaker || k.nama_ustaz || "",
                    subjectText: k.subjek || "Umum",
                    titleText: k.tajuk || k.title || "",
                };
            })
            .filter((k) => k.date instanceof Date && !isNaN(k.date.getTime()))
            .filter((k) => k.date.getTime() > now)
            .sort((a, b) => a.date - b.date)
            .slice(0, 12);
    }, [masjid, masjidId, subSingles, subRecurring]);



    const getKuliahKey = useCallback((k) => {
        const d = getEventDate(k);
        const sid = k?.seriesId || k?.seriesKey;

        if (sid && d) return `${String(sid)}__${toYMD(d)}`;
        if (k?.eventId) return String(k.eventId);

        const ms = getStartMs(k);
        return ms ? `t__${ms}` : "";
    }, []);






    const hasKuliah = kuliahUpcoming.length > 0;

    const handledKRef = useRef(false);

    const kParam = useMemo(() => {
        const sp = new URLSearchParams(location.search);
        return sp.get("k") || "";
    }, [location.search]);

    useEffect(() => {
        if (!kParam) return;
        if (handledKRef.current) return;

        // tunggu data kuliah siap (kuliahUpcoming dah compute)
        if (!kuliahUpcoming || kuliahUpcoming.length === 0) return;

        // cari match
        const found = kuliahUpcoming.find((x) => getKuliahKey(x) === kParam);

        handledKRef.current = true;

        if (!found) {
            // kalau tak jumpa, biar je stay di home (atau boleh show toast)
            return;
        }

        // navigate ke single & buang query (?k=) dari URL
        navigate(`/l/${encodeURIComponent(masjidSlug)}/k/${encodeURIComponent(kParam)}`, {
            replace: true,
            state: { masjid, kuliah: found, kKey: kParam },
        });
    }, [kParam, kuliahUpcoming, masjidSlug, masjid, navigate, getKuliahKey]);


    // ===== ETV
    const etvImages = useMemo(() => {
        const imgs = masjid?.etv?.etvImage || [];
        if (Array.isArray(imgs)) return imgs.filter(Boolean);
        return Object.values(imgs || {}).filter(Boolean);
    }, [masjid]);

    const etvSig = useMemo(() => etvImages.join("|"), [etvImages]);

    const etvImagesRef = useRef([]);
    useEffect(() => {
        etvImagesRef.current = etvImages;
    }, [etvImages]);


    useEffect(() => {
        const imgs = etvImagesRef.current || [];
        if (imgs.length > 0) {
            setActiveEtvIndex(0);
            setActiveEtvHero(imgs[0]);
        } else {
            setActiveEtvIndex(0);
            setActiveEtvHero(null);
        }
    }, [etvSig]);


    const closeZoom = () => {
        setEtvZoomOpen(false);

        // sambung balik auto selepas 400ms (bagi animation settle)
        setTimeout(() => {
            zoomPausedRef.current = false;
            autoScrollPausedRef.current = false;
        }, 400);
    };


    useEffect(() => {
        const el = stripRef.current;
        const imgs = etvImagesRef.current || [];
        if (!el) return;
        if (imgs.length <= 1) return;

        const pauseAuto = () => {
            autoScrollPausedRef.current = true;
            if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
            pauseTimerRef.current = setTimeout(() => {
                autoScrollPausedRef.current = false;
            }, 4000);
        };

        el.addEventListener("touchstart", pauseAuto, { passive: true });
        el.addEventListener("pointerdown", pauseAuto, { passive: true });
        el.addEventListener("wheel", pauseAuto, { passive: true });

        const t = window.setInterval(() => {
            if (autoScrollPausedRef.current) return;
            if (zoomPausedRef.current) return;   // ✅ pause bila zoom open

            const list = etvImagesRef.current || [];
            if (list.length <= 1) return;

            setActiveEtvIndex((prev) => (prev + 1) % list.length);
        }, 3000);

        return () => {
            clearInterval(t);
            el.removeEventListener("touchstart", pauseAuto);
            el.removeEventListener("pointerdown", pauseAuto);
            el.removeEventListener("wheel", pauseAuto);
            if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
        };
    }, [etvSig]);


    useEffect(() => {
        const imgs = etvImagesRef.current || [];
        if (imgs.length === 0) return;

        const img = imgs[activeEtvIndex];
        if (img) setActiveEtvHero(img);

        const el = stripRef.current;
        if (!el) return;

        const node = el.querySelector(`[data-etv-idx="${activeEtvIndex}"]`);
        if (!node) return;

        const left = node.offsetLeft - el.clientWidth / 2 + node.clientWidth / 2;
        el.scrollTo({ left, behavior: "smooth" });
    }, [activeEtvIndex, etvSig]);



    const copyAccount = async (text, index) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedIndex(index);
            setTimeout(() => setCopiedIndex(null), 1200);
        } catch (err) {
            console.error("Copy failed", err);
        }
    };




    // ✅ ok sekarang baru decide UI
    if (!masjid) {
        return <div className="masjid-empty" style={{ padding: 18 }}>Loading...</div>;
    }

    return (
        <>

            <div className="mp-container">
                <MasjidAzanCard masjid={masjid} />
            </div>
            {/* SHORTCUTS */}
            <div className="mp-container" style={{ marginTop: 0 }}>
                <div className="card-content">

                    <div className="masjid-shortcuts clean">
                        {gembed && (
                            <button
                                className="shortcut-clean"
                                onClick={() => mapRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                            >
                                <FiMapPin size={20} />
                                <span>Lokasi</span>
                            </button>
                        )}

                        {hasPegawai && (
                            <button
                                className="shortcut-clean"
                                onClick={() => pengurusanRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                            >
                                <FiUsers size={20} />
                                <span>Pengurusan</span>
                            </button>
                        )}

                        {hasHubungi && (
                            <button
                                className="shortcut-clean"
                                onClick={() => hubungiRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                            >
                                <FiMessageCircle size={20} />
                                <span>Hubungi</span>
                            </button>
                        )}

                        {hasKempen && (
                            <button
                                className="shortcut-clean"
                                onClick={() => kempenRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                            >
                                <FiHeart size={20} />
                                <span>Kempen</span>
                            </button>
                        )}

                        {hasBank && (
                            <button
                                className="shortcut-clean"
                                onClick={() => bankRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                            >
                                <FiCreditCard size={20} />
                                <span>Bank</span>
                            </button>
                        )}

                        {hasKuliah && (
                            <button
                                className="shortcut-clean"
                                onClick={() => kuliahRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                            >
                                <FiBookOpen size={20} />
                                <span>Kuliah</span>
                            </button>
                        )}

                        {hasMediaSosial && (
                            <button className="shortcut-clean" onClick={() => mediaRef.current?.scrollIntoView({ behavior: "smooth" })}>
                                <FiGlobe />
                                <span>Media</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>



            {/* ETV */}
            <div className="column-body etv">
                {etvImages.length === 0 ? (
                    <div className="masjid-empty">Tiada poster ETV lagi.</div>
                ) : (
                    <div className="etv-stack">


                        <div
                            className="etv-hero"
                            role="button"
                            onClick={() => {
                                zoomPausedRef.current = true;
                                autoScrollPausedRef.current = true;
                                setEtvZoomOpen(true);
                            }}
                        >
                            <img src={activeEtvHero} alt="ETV Hero" />
                        </div>



                        <div className="etv-strip" ref={stripRef}>
                            {etvImages.map((img, idx) => (
                                <div
                                    key={idx}
                                    data-etv-idx={idx}
                                    className={`etv-thumb ${idx === activeEtvIndex ? "active" : ""}`}
                                    onClick={() => {
                                        setActiveEtvIndex(idx);
                                        setActiveEtvHero(img);
                                        autoScrollPausedRef.current = true;
                                        if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
                                        pauseTimerRef.current = setTimeout(() => {
                                            autoScrollPausedRef.current = false;
                                        }, 4000);
                                    }}
                                >
                                    <img src={img} alt="" />
                                </div>
                            ))}
                        </div>

                    </div>
                )}
            </div>


            {hasKempen && (
                <div className="column-body" ref={kempenRef}>
                    <div className="title-area">KEMPEN</div>
                    <PublicKempenFeatured masjid={masjid} kempenList={kempenList} fallbackImage={bannerImg} />
                </div>
            )}



            {/* Pengurusan */}
            {hasPegawai && (
                <div className="column-body" ref={pengurusanRef}>
                    <div className="title-area">PENGURUSAN MASJID</div>

                    <div className="pegawai-row">
                        {pegawaiGroups.map((group) => (
                            <div key={group.key} className="pegawai-kategori">
                                <div className="pegawai-kategori-title">{group.label}</div>

                                <div className="pegawai-scroll">
                                    {group.items.map((peg, idx) => (
                                        <div className="pegawai-card" key={`${peg.id || idx}`}>
                                            <div className="pegawai-role">
                                                {peg.jawatan || "—"}
                                            </div>

                                            <img
                                                src={peg.gambar || "/images/150.png"}
                                                alt={peg.nama || ""}
                                                loading="lazy"
                                            />

                                            <div className="pegawai-name">
                                                {peg.nama || ""}
                                            </div>
                                        </div>

                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Kuliah */}
            {/* =========================
    KULIAH (LIST + SINGLE VIEW via ?k=...)
========================= */}
            <div className="column-body kuliah" ref={kuliahRef}>
                <div className="title-area">KULIAH PENGAJIAN</div>

                {kuliahUpcoming.length === 0 ? (
                    <div className="masjid-empty">Tiada kuliah akan datang buat masa ini.</div>
                ) : (
                    <div className="cards-container" style={{ alignItems: "stretch" }}>
                        {kuliahUpcoming.map((kuliah, index) => {
                            const banner = kuliah.poster || bannerImg;

                            const shareText = [
                                `Jom hadir ${kuliah.programText} di ${masjidName}`,
                                kuliah.date ? `${getHariLong(kuliah.date)}, ${getTarikh(kuliah.date)}` : "",
                                kuliah.ustazText ? `Ustaz: ${kuliah.ustazText}` : "",
                                kuliah.titleText ? `Tajuk: ${kuliah.titleText}` : "",
                            ]
                                .filter(Boolean)
                                .join("\n");

                            const kKey = getKuliahKey(kuliah);
                            if (!kKey) return null;

                            // ✅ share terus ke page single view
                            const shareUrl = `${window.location.origin}/l/${encodeURIComponent(masjidSlug)}/k/${encodeURIComponent(kKey)}`;


                            const shareHref = waShareLink(shareText, shareUrl);

                            return (
                                <div
                                    className="card"
                                    key={`${kuliah.eventId || index}`}
                                    style={{ display: "flex", flexDirection: "column" }}
                                    role="button"
                                    onClick={() => {
                                        navigate(`/l/${encodeURIComponent(masjidSlug)}/k/${encodeURIComponent(kKey)}`, {
                                            state: {
                                                masjid,          // masjid object yg dah ada
                                                kuliah,          // kuliah item yg user klik
                                                kKey,            // key yang sama dgn param :k
                                            },
                                        });
                                    }}


                                >
                                    <div className="card-image">
                                        {banner ? <img src={banner} alt="Kuliah" /> : <div className="card-image-fallback" />}
                                    </div>

                                    <div
                                        className="card-content"
                                        style={{ flex: "1 1 auto", display: "flex", flexDirection: "column" }}
                                    >
                                        <div className="mosque-row">
                                            {masjidLogo && (
                                                <img
                                                    className="mosque-logo"
                                                    src={masjidLogo}
                                                    alt="Logo masjid"
                                                    onError={(e) => (e.currentTarget.style.display = "none")}
                                                />
                                            )}
                                            <p className="mosque-name-body">{masjidName}</p>
                                        </div>

                                        <div className="card-header">
                                            <h3>{kuliah.programText}</h3>
                                            <p className="date">
                                                {kuliah.date ? `${getHariLong(kuliah.date)}, ${getTarikh(kuliah.date)}` : ""}
                                            </p>
                                            <p className="ustaz-name">{kuliah.ustazText}</p>
                                        </div>

                                        <div
                                            className={`card-footer ${kuliah.kitabCoverImage ? "has-kitab" : ""}`}
                                            style={{ minHeight: 62, alignItems: "start" }}
                                        >
                                            {kuliah.kitabCoverImage && (
                                                <img
                                                    className="kitab-cover"
                                                    src={kuliah.kitabCoverImage}
                                                    alt={kuliah.kitabTitle || "Kitab"}
                                                    onError={(e) => (e.currentTarget.style.display = "none")}
                                                />
                                            )}

                                            <div className="footer-right">
                                                <div className="subject">
                                                    <span className="subject-label">{kuliah.subjectText}</span>
                                                </div>
                                                <p className="title">{kuliah.titleText}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ marginTop: "auto", margin: 10, display: "flex", gap: 8 }}>
                                        <a
                                            href={shareHref}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="kuliah-share-btn"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <FaWhatsapp />
                                            <span>Share</span>
                                        </a>
                                    </div>

                                    <div className="avatar-wrapper">
                                        <div className="avatar">
                                            <img
                                                src={kuliah.speakerImage || kuliah.gambar_ustaz || ""}
                                                alt="Ustaz"
                                                onError={(e) => (e.currentTarget.style.display = "none")}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>








            <div style={{ display: "flex" }}>
                <div style={{ flex: 4 }}>
                    {gembed && (
                        <div className="column-body" ref={mapRef} >
                            <div className="title-area">LOKASI</div>

                            <div className="masjid-map">
                                <iframe title="map" src={gembed} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                            </div>
                        </div>
                    )}
                    <div style={{ paddingLeft: " 10px", display: " flex", alignItems: " center", gap: "10px" }}>
                        <FaLocationPin />

                        {addressLine && <div style={{ margin: 0 }}>{addressLine}</div>}

                    </div>
                </div>



            </div>

            <div className="hubungi-fb">
                <div style={{ flex: 1 }}>
                    {hasHubungi && (
                        <div className="column-body hubungi-col" ref={hubungiRef}>
                            <div className="title-area">HUBUNGI</div>

                            <div className="hubungi-grid">
                                {phone && (
                                    <a href={`tel:${cleanPhone(phone)}`} className="hubungi-tile">
                                        <FiPhone />
                                        <span>Telefon</span>
                                    </a>
                                )}

                                {contactLink && (
                                    <a href={contactLink} target="_blank" rel="noreferrer" className="hubungi-tile">
                                        <FaWhatsapp />
                                        <span>{contactMode === "messenger" ? "Messenger" : "WhatsApp"}</span>
                                    </a>
                                )}

                                {email && (
                                    <a href={`mailto:${email}`} className="hubungi-tile">
                                        <FiMail />
                                        <span>Email</span>
                                    </a>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="hubungi-media">


                        {hasMediaSosial && (
                            <div className="column-body hubungi-col" ref={mediaRef}>
                                <div className="title-area">MEDIA</div>

                                <div className="hubungi-grid">
                                    {p?.facebook && (
                                        <a href={p.facebook} target="_blank" rel="noreferrer" className="hubungi-tile">
                                            <FaFacebook />
                                            <span>Facebook</span>
                                        </a>
                                    )}

                                    {p?.instagram && (
                                        <a href={p.instagram} target="_blank" rel="noreferrer" className="hubungi-tile">
                                            <FaInstagram />
                                            <span>Instagram</span>
                                        </a>
                                    )}

                                    {p?.youtube && (
                                        <a href={p.youtube} target="_blank" rel="noreferrer" className="hubungi-tile">
                                            <FaYoutube />
                                            <span>YouTube</span>
                                        </a>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>


                    {hasBank && (
                        <div className="column-body" ref={bankRef} style={{ flex: 1 }}>
                            <div className="title-area">BANK</div>

                            <div className="derma-wrapper">
                                {dermaQr && (
                                    <div className="derma-qr">
                                        <img src={dermaQr} alt="QR Derma" onError={(e) => (e.currentTarget.style.display = "none")} />
                                        <div className="derma-qr-text">QR Duintnow</div>
                                    </div>
                                )}

                                {bankList.length > 0 && (
                                    <div className="derma-bank-list">
                                        {bankList.map((b, i) => (
                                            <div className="derma-bank" key={i}>
                                                <div className="bank-name">{b.bank}</div>

                                                <div className="bank-akaun-row">
                                                    <div className="bank-akaun">{b.account}</div>

                                                    <button
                                                        className="copy-btn"
                                                        onClick={() => copyAccount(b.account, i)}
                                                        aria-label="Copy account number"
                                                    >
                                                        {copiedIndex === i ? <FiCheck /> : <FiCopy />}
                                                    </button>
                                                </div>

                                                {b.holder && <div className="bank-holder">{b.holder}</div>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>


                {p?.facebook && (
                    <div style={{ backgroundColor: "#375798", padding: "20px", marginTop: "20px" }}>
                        <FbTimelineWidget pageUrl={p.facebook} />
                    </div>
                )}
            </div>


            {showToTop && (
                <button className="to-top-fab" onClick={scrollToTop} aria-label="Naik ke atas">
                    <FiArrowUp />
                </button>
            )}

            {etvZoomOpen && (
                <div className="etv-zoom" onClick={closeZoom}>
                    <button
                        className="etv-zoom-close"
                        onClick={(e) => {
                            e.stopPropagation();
                            closeZoom();
                        }}
                        aria-label="Tutup"
                    >
                        ✕
                    </button>

                    <img
                        className="etv-zoom-img"
                        src={activeEtvHero}
                        alt="ETV Full"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}




        </>
    );
}
